import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseForm } from '../../../../base-form';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { build, pool } from 'src/app/common/classes/request-builder';
import { FitoLocalesFormDefinition } from './form-control/form-definition';
import {
    ReactiveFormChangedField,
    ReactiveFormValue,
    DropdownData,
    Value,
    FormType,
} from '@fitogest/reactive-form-builder/interfaces';
import type { FitoLocalesModel } from 'src/app/models/tareas/fito-locales.model';
import type { ProductosModel } from 'src/app/models/productos/productos.model';
import type { FincasModel } from 'src/app/models/form-common/fincas.module';

@Component({
    selector: 'app-fito-locales-form',
    standalone: false,
    templateUrl: './fito-locales-form.component.html',
    styleUrls: ['./fito-locales-form.component.scss'],
})
export class FitoLocalesFormComponent extends BaseForm<FitoLocalesModel> implements OnInit {
    public form!: FitoLocalesFormDefinition;
    public override model: FitoLocalesModel = {};
    public formReady = false;
    public loadingData = true;

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public clientesApi: ClientesApiService,
        public fincasApi: FincasApiService,
        public productosApi: ProductosApiService,
        public tareasApi: TareasApiService,
        public override router: Router,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.fitoLocales.PUT,
            tareasApi.fitoLocales.POST,
            'fito-locales',
            'Editar Tarea Fitosanitaria en locales',
            'Crear Tarea Fitosanitaria en locales',
            'Duplicar Tarea Fitosanitaria en locales',
        );
    }

    public ngOnInit(): void {
        this.softInit(this.getType());
        this.model = { ...this.getModel() };
        this.form = new FitoLocalesFormDefinition(this.getType(), this.model);

        void this.initFormData().then(() => {
            this.applyCreationDefaults();
            this.formReady = true;
        });
    }

    public formChanges(
        formModel: ReactiveFormValue<FitoLocalesModel>,
        lastChangedFieldName: ReactiveFormChangedField<FitoLocalesModel>,
    ): void {
        this.form.model = {
            ...this.form.model,
            ...formModel,
        };
        this.model = this.form.model as FitoLocalesModel;

        if (String(lastChangedFieldName) === 'id_cliente') {
            this.formBuilderRef?.updateForm({ id_finca: '' }, true);
        }
    }

    public afterSend(): void {
        if (this.getType() === FormType.DUPLICATION) {
            window.location.reload();
        }
    }

    private initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        return pool([
            build(this.clientesApi.clientesGET, this.form.clientes, 'nombre'),
            build(this.fincasApi.fincasGET, this.form.fincas, 'nombre', true),
            build(this.productosApi.productos.GET, this.form.productos, 'nombre'),
        ], () => {
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.form.productos.values = this.filterProductosFitosanitarios(this.form.productos);
            this.form.productos.filtered = this.filterProductosFitosanitarios(this.form.productos);
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.dropdownLoadTrigger.next();

            return true;
        }).catch((error: unknown) => {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.dropdownLoadTrigger.next();
            console.error('Error loading fito locales form data.', error);

            return true;
        });
    }

    private filterProductosFitosanitarios(productos: DropdownData<ProductosModel>): Value<ProductosModel>[] {
        const filtered: Value<ProductosModel>[] = [{ label: '...', value: null }];

        for (const producto of productos.values ?? []) {
            if (producto.value?.tipo === 'fitosanitario') {
                filtered.push(producto);
            }
        }

        return filtered;
    }

    private applyCreationDefaults(): void {
        if (this.getType() !== FormType.CREATION || this.form.fincas.values.length !== 2) {
            return;
        }

        const onlyFinca = this.form.fincas.values[1]?.value as FincasModel | null | undefined;

        if (!onlyFinca?.id) {
            return;
        }

        this.form.model.id_finca = String(onlyFinca.id);
        this.formBuilderRef?.updateForm({ id_finca: onlyFinca }, true);
    }
}
